import {computed, nextTick, ref, useTemplateRef, watch} from "vue";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import {QInput} from "quasar";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import SearchingAndSuggestionUtil from "@/utils/editView/widgets/SearchingAndSuggestionUtil.ts";
import Translator from "@/utils/common/Translator.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import ExpressionEditUtility from "@/utils/common/ExpressionEditUtility.ts";
import QuestionField = namaTypes.QuestionField;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import AllowedValue = namaTypes.AllowedValue;

export type MenuItem = { id: string; code: string; caption: string; value: any; searchValue: string };

const CHIPS_SEARCH_THRESHOLD = 12;

export function useQuestionList(presenter: WidgetsPresenter, questionField: QuestionField) {
  const fieldId: string = questionField?.id ?? '';

  const selectedDetails = computed<any[]>(() => {
    const details = presenter?.fetchRecord()[fieldId]?.[QuestionFieldsPresenter.detailId];
    if (details?.length > 1 || details?.[0]?.[fieldId])
      return details;
    return [];
  });
  const selectedRowsNumber = computed(() => selectedDetails.value.length);

  function getDetailText(detail: any): string {
    const value = detail?.[fieldId];
    if (ObjectChecker.isNullOrUndefined(value))
      return '';
    if (ObjectChecker.isObject(value))
      return ObjectChecker.getFirstNotEmptyObj(value.description, value.code, value.text, value.value) ?? '';
    return String(value);
  }

  function removeDetail(detail: any) {
    const details: any[] = presenter.fetchRecord()[fieldId]?.[QuestionFieldsPresenter.detailId] ?? [];
    const idx = details.findIndex(d => d.id === detail.id);
    if (idx >= 0)
      details.splice(idx, 1);
  }

  const searchRef = useTemplateRef<QInput>("searchRef");
  const searchText = ref('');
  const suggestItems = ref<MenuItem[]>([]);
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const hasMoreSuggestions = ref(false);
  const chipsSearchLatched = ref(false);
  let suggestPage = 0;
  let suggestSeq = 0;
  let searchDebounceTimer: number | undefined;
  let skipNextSearchFetch = false;

  const allowedItems = computed<MenuItem[]>(() => {
    const isSubsidiaryTypeField = questionField.metaData?.enumTypeName === 'SubsidiaryType';
    const typesShownInReports = ExpressionEditUtility.fetchSubsidiaryTypesShownInReports(presenter);
    const isShown = (value: string | undefined) =>
      !isSubsidiaryTypeField || ExpressionEditUtility.isSubsidiaryTypeShown(value, typesShownInReports);
    if (ObjectChecker.isNotEmptyOrNull(questionField.allowedValues)) {
      return questionField.allowedValues.filter((a: AllowedValue) => isShown(a.value ?? a.id)).map((a: AllowedValue) => {
        const id = a.id ?? a.value ?? '';
        const caption = a.title ? Translator.translateNaMaText(a.title) : Translator.translateId(id);
        return {id, code: id, caption, value: id, searchValue: (caption + ' ' + id).toLowerCase()};
      });
    }
    const md = questionField.metaData;
    if (!md)
      return [];
    const values = GUIUtilities.getAllowedValues(presenter.entityType, md);
    const enumName = md.enumTypeName ?? '';
    return (values ?? []).filter(v => isShown(v)).map(v => {
      const caption = enumName ? Translator.translateId(enumName + '.' + v) : Translator.translateId(v);
      return {id: v, code: v, caption, value: v, searchValue: (caption + ' ' + v).toLowerCase()};
    });
  });

  const useSuggestApi = computed(() =>
    ObjectChecker.isEmptyOrNull(questionField.allowedValues) && allowedItems.value.length === 0
  );

  const filteredAllowedItems = computed<MenuItem[]>(() => {
    const q = searchText.value?.trim().toLowerCase();
    if (!q)
      return allowedItems.value;
    const normalized = StringUtils.normalizeArabic(q);
    return allowedItems.value.filter(i =>
      i.searchValue.includes(q) || i.searchValue.includes(normalized)
    );
  });

  const displayedItems = computed<MenuItem[]>(() => useSuggestApi.value ? suggestItems.value : filteredAllowedItems.value);
  const fieldTitle = computed(() => Translator.translateTitledId(presenter.entityType, questionField));

  function resolveReferencedEntityType(): string | undefined {
    let referencedEntityType: string | undefined = questionField.metaData?.referencedEntityType;
    if (referencedEntityType && referencedEntityType.startsWith('$')) {
      const paramName: string = referencedEntityType.substring(1);
      const paramValue = presenter.fetchRecord()[paramName];
      referencedEntityType = ObjectChecker.isNotEmptyOrNull(paramValue) ? String(paramValue) : "";
    }
    return referencedEntityType;
  }

  function mapSearchResult(r: any): MenuItem {
    const code = r.code ?? '';
    const caption = r['optionCaption'] ?? Translator.getNameForTitle(r) ?? '';
    return {
      id: r.id ?? code,
      code,
      caption,
      value: r,
      searchValue: (code + ' ' + caption).toLowerCase(),
    };
  }

  async function searchMenuItems(query: string, page = 0): Promise<MenuItem[]> {
    const md = questionField.metaData;
    if (!md)
      return [];
    const referencedEntityType = resolveReferencedEntityType() ?? '';
    const ownerEntityType = presenter.entityType;
    const contextInfo: FieldActionContextInfo = presenter.createSearchContextInfo(fieldId);
    const extraCriteria = presenter.getDefaultCriteria(fieldId);
    const results = await SearchingAndSuggestionUtil.searchFor({
      ownerEntityType,
      referencedEntityType,
      fieldId,
      contextInfo,
      query,
      extraCriteria,
      page,
    });
    return (results ?? []).map(mapSearchResult);
  }

  function isFullPage(items: MenuItem[]): boolean {
    return items.length >= SearchingAndSuggestionUtil.SEARCH_PAGE_SIZE;
  }

  async function fetchSuggestions(query: string) {
    if (!questionField.metaData)
      return;
    const seq = ++suggestSeq;
    isLoading.value = true;
    try {
      const items = await searchMenuItems(query);
      if (seq !== suggestSeq) return;
      suggestItems.value = items;
      suggestPage = 0;
      hasMoreSuggestions.value = isFullPage(items);
      if (hasMoreSuggestions.value || items.length > CHIPS_SEARCH_THRESHOLD)
        chipsSearchLatched.value = true;
    } finally {
      if (seq === suggestSeq) isLoading.value = false;
    }
  }

  async function fetchMoreSuggestions() {
    if (!hasMoreSuggestions.value || isLoadingMore.value || isLoading.value)
      return;
    const seq = suggestSeq;
    isLoadingMore.value = true;
    try {
      const items = await searchMenuItems(searchText.value ?? '', suggestPage + 1);
      if (seq !== suggestSeq) return;
      const known = new Set(suggestItems.value.map(i => i.id));
      const fresh = items.filter(i => !known.has(i.id));
      suggestItems.value = [...suggestItems.value, ...fresh];
      suggestPage++;
      hasMoreSuggestions.value = isFullPage(items) && fresh.length > 0;
    } finally {
      isLoadingMore.value = false;
    }
  }

  const isChipsSearchVisible = computed(() =>
    chipsSearchLatched.value || searchText.value !== '' || allowedItems.value.length > CHIPS_SEARCH_THRESHOLD
  );

  watch(searchText, (q) => {
    if (!useSuggestApi.value) return;
    if (skipNextSearchFetch) {
      skipNextSearchFetch = false;
      return;
    }
    if (searchDebounceTimer) window.clearTimeout(searchDebounceTimer);
    searchDebounceTimer = window.setTimeout(() => fetchSuggestions(q ?? ''), 250);
  });

  function isItemSelected(item: MenuItem): boolean {
    return selectedDetails.value.some(d => {
      return ObjectChecker.areEqual(d?.[fieldId], item.value)
    });
  }

  const allSelected = computed(() => displayedItems.value.length > 0 && displayedItems.value.every(i => isItemSelected(i)));
  const someSelected = computed(() => !allSelected.value && displayedItems.value.some(i => isItemSelected(i)));

  async function toggleAll() {
    const rec = presenter.fetchRecord();
    const record = rec[fieldId] ?? (rec[fieldId] = {[QuestionFieldsPresenter.detailId]: []});
    const details: any[] = record[QuestionFieldsPresenter.detailId] ?? (record[QuestionFieldsPresenter.detailId] = []);
    if (allSelected.value) {
      const displayed = new Set(displayedItems.value.map(i => i.id));
      for (let idx = details.length - 1; idx >= 0; idx--) {
        const stored = details[idx]?.[fieldId];
        const storedId = stored == null ? '' : typeof stored === 'object' ? String(stored.id ?? stored.code ?? '') : String(stored);
        if (displayed.has(storedId))
          details.splice(idx, 1);
      }
    } else {
      for (const item of displayedItems.value) {
        if (!isItemSelected(item)) {
          const storedValue = useSuggestApi.value ? buildReferenceValue(item) : item.id;
          details.push({id: await ClientCache.asyncNextGUIDId(), [fieldId]: storedValue});
        }
      }
    }
  }

  async function toggleItem(item: MenuItem) {
    const rec = presenter.fetchRecord();
    const record = rec[fieldId] ?? (rec[fieldId] = {[QuestionFieldsPresenter.detailId]: []});
    const details: any[] = record[QuestionFieldsPresenter.detailId] ?? (record[QuestionFieldsPresenter.detailId] = []);
    const idx = details.findIndex(d => {
      return ObjectChecker.areEqual(d?.[fieldId], item.value)
    });
    if (idx >= 0) {
      details.splice(idx, 1);
    } else {
      const storedValue = useSuggestApi.value ? buildReferenceValue(item) : item.id;
      details.push({id: await ClientCache.asyncNextGUIDId(), [fieldId]: storedValue});
    }
  }

  function buildReferenceValue(item: MenuItem): any {
    const r = item.value ?? {};
    return {
      entityType: r.entityType ?? resolveReferencedEntityType(),
      id: r.id ?? item.id,
      code: r.code ?? item.code,
      altCode: r.altCode,
      name1: r.name1,
      name2: r.name2,
      description: r['optionCaption'] ?? Translator.getNameForTitle(r) ?? item.caption,
    };
  }

  function isReferenceWithoutEntityType(): boolean {
    return questionField.metaData?.fieldType === FieldType.Reference && !resolveReferencedEntityType();
  }

  function selectedDetailsAsMenuItems(): MenuItem[] {
    return selectedDetails.value.map(d => {
      const value = d?.[fieldId];
      if (ObjectChecker.isObject(value)) {
        const code = value.code ?? '';
        const caption = ObjectChecker.getFirstNotEmptyObj(value.description, value.name1, value.name2, value.text) ?? '';
        return {id: value.id ?? code, code, caption, value, searchValue: (code + ' ' + caption).toLowerCase()};
      }
      const code = String(value ?? '');
      return {id: code, code, caption: '', value, searchValue: code.toLowerCase()};
    });
  }

  const chipItems = computed<MenuItem[]>(() => {
    const displayed = displayedItems.value;
    const displayedIds = new Set(displayed.map(i => i.id));
    const selectedSource = useSuggestApi.value
      ? selectedDetailsAsMenuItems()
      : allowedItems.value.filter(i => isItemSelected(i));
    const pinnedSelected = selectedSource.filter(i => !displayedIds.has(i.id));
    return pinnedSelected.length ? [...pinnedSelected, ...displayed] : displayed;
  });

  async function onPlusClick(e: Event) {
    if (isReferenceWithoutEntityType()) {
      useErrorsStore().pushErrorStr("You must select Entity Type");
      return;
    }
    if (useSuggestApi.value) {
      if (selectedDetails.value.length > 0) {
        if (searchText.value !== '') {
          skipNextSearchFetch = true;
          searchText.value = '';
        }
        suggestItems.value = selectedDetailsAsMenuItems();
      } else {
        searchText.value = '';
        await fetchSuggestions('');
      }
    } else {
      searchText.value = '';
    }
    await nextTick(() => searchRef.value?.focus());
  }

  async function loadItems() {
    if (isReferenceWithoutEntityType()) return;
    if (useSuggestApi.value)
      await fetchSuggestions('');
  }

  function normalizeForMatch(text: string): string {
    return StringUtils.normalizeArabic((text ?? '').trim().toLowerCase());
  }

  function matchInMemory(line: string): MenuItem | undefined {
    const normalized = normalizeForMatch(line);
    if (!normalized) return undefined;
    return allowedItems.value.find(i => normalizeForMatch(i.code) === normalized)
      ?? allowedItems.value.find(i => normalizeForMatch(i.caption) === normalized);
  }

  async function matchViaSuggest(line: string): Promise<MenuItem | undefined> {
    const normalized = normalizeForMatch(line);
    if (!normalized) return undefined;
    const results = await searchMenuItems(line);
    return results.find(i => normalizeForMatch(i.code) === normalized)
      ?? results.find(i => normalizeForMatch(i.caption) === normalized);
  }

  async function pasteAndSelect(text: string) {
    if (isReferenceWithoutEntityType()) {
      useErrorsStore().pushErrorStr("You must select Entity Type");
      return;
    }
    const lines = [...new Set((text ?? '').split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0))];
    if (lines.length === 0)
      return;
    const rec: Record<string, any> = presenter.fetchRecord();
    const record = rec[fieldId] ?? (rec[fieldId] = {[QuestionFieldsPresenter.detailId]: []});
    const details: any[] = record[QuestionFieldsPresenter.detailId] ?? (record[QuestionFieldsPresenter.detailId] = []);
    const unmatched: string[] = [];
    for (const line of lines) {
      const match = useSuggestApi.value ? await matchViaSuggest(line) : matchInMemory(line);
      if (!match) {
        unmatched.push(line);
        continue;
      }
      if (isItemSelected(match))
        continue;
      const storedValue = useSuggestApi.value ? buildReferenceValue(match) : match.id;
      details.push({id: await ClientCache.asyncNextGUIDId(), [fieldId]: storedValue});
    }
    if (unmatched.length > 0)
      useErrorsStore().pushErrorStr(Translator.translateId('followingValuesWereNotFoundInTheList') + ': ' + unmatched.join(', '));
  }

  function detailCodeAndCaption(detail: any): { code: string; caption: string } {
    const value = detail?.[fieldId];
    if (ObjectChecker.isNullOrUndefined(value))
      return {code: '', caption: ''};
    if (ObjectChecker.isObject(value)) {
      const code = ObjectChecker.getFirstNotEmptyObj(value.code, value.id) ?? '';
      const caption = ObjectChecker.getFirstNotEmptyObj(value.description, value.text, value.value) ?? '';
      return {code: String(code), caption: String(caption)};
    }
    const code = String(value);
    const caption = allowedItems.value.find(i => i.code === code)?.caption ?? '';
    return {code, caption};
  }

  function selectionAsText(): string {
    return selectedDetails.value.map(d => getDetailText(d)).join('\n');
  }

  async function setSelectionFromText(text: string) {
    const lines = [...new Set((text ?? '').split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0))];
    const rec = presenter.fetchRecord();
    const record = rec[fieldId] ?? (rec[fieldId] = {[QuestionFieldsPresenter.detailId]: []});
    const rows: any[] = [];
    for (const line of lines)
      rows.push({id: await ClientCache.asyncNextGUIDId(), [fieldId]: line});
    record[QuestionFieldsPresenter.detailId] = rows;
  }

  async function copySelected() {
    if (selectedDetails.value.length === 0) return;
    const text = selectedDetails.value
      .map(d => {
        const {code, caption} = detailCodeAndCaption(d);
        return code;
      })
      .join('\n');
    await navigator.clipboard.writeText(text);
  }

  return {
    fieldId,
    selectedDetails,
    selectedRowsNumber,
    getDetailText,
    removeDetail,
    searchRef,
    searchText,
    suggestItems,
    isLoading,
    isLoadingMore,
    hasMoreSuggestions,
    fetchMoreSuggestions,
    isChipsSearchVisible,
    chipItems,
    allowedItems,
    useSuggestApi,
    filteredAllowedItems,
    displayedItems,
    fieldTitle,
    resolveReferencedEntityType,
    isItemSelected,
    allSelected,
    someSelected,
    toggleAll,
    toggleItem,
    isReferenceWithoutEntityType,
    onPlusClick,
    loadItems,
    pasteAndSelect,
    copySelected,
    selectionAsText,
    setSelectionFromText,
  };
}
